import * as THREE from 'three';
import { TERRAIN, type GameMap } from '@sunderfen/shared';
import { stylize } from './stylize.js';
import type { Walker } from './critters.js';

/**
 * Wild things that are only scenery: small groups of deer grazing at the edges of woods and
 * meadows, and the odd fox (more of them at dusk and at night). They graze, lift their heads
 * when someone comes near, and bound away if you keep coming. Purely client-side: nothing to
 * click, nothing the server knows about. A handful at a time, near the camera.
 */

const WILD_REGIONS = new Set(['downs', 'birchwood', 'lake', 'fen', 'grove', 'copperback', 'shore', 'cove', 'rustcliff', 'ashen']);

type Kind = 'deer' | 'fox';
interface Beast {
  kind: Kind; g: THREE.Group; head: THREE.Object3D; legs: THREE.Object3D[]; tail: THREE.Object3D;
  x: number; z: number; face: number; state: 'graze' | 'alert' | 'walk' | 'flee'; t: number; gait: number; speed: number; dir: number; gone: boolean;
}

const mat = (c: number) => stylize(new THREE.MeshLambertMaterial({ color: c }));
/** A smooth, slightly tapered body segment along +z. */
function pod(len: number, r: number, taper = 0.8, seg = 10) {
  const pts: THREE.Vector2[] = [];
  for (let i = 0; i <= seg; i++) { const t = i / seg, y = (t - 0.5) * len, rr = r * Math.sin(Math.PI * Math.min(1, 0.06 + t * 0.94)) * (1 - (1 - taper) * t); pts.push(new THREE.Vector2(Math.max(0.001, rr), y)); }
  const g = new THREE.LatheGeometry(pts, 10); g.rotateX(Math.PI / 2); return g;
}
function leg(parent: THREE.Object3D, x: number, y: number, z: number, h: number, r: number, m: THREE.Material, hoof: THREE.Material) {
  const pivot = new THREE.Group(); pivot.position.set(x, y, z); parent.add(pivot);
  const upper = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 0.7, h * 0.55, 6), m); upper.position.y = -h * 0.27; pivot.add(upper);
  const lower = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.6, r * 0.45, h * 0.5, 6), m); lower.position.y = -h * 0.75; pivot.add(lower);
  const foot = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.5, r * 0.55, h * 0.07, 6), hoof); foot.position.y = -h * 0.99; pivot.add(foot);
  return pivot;
}

export function buildDeer(stag: boolean): Omit<Beast, 'x' | 'z' | 'face' | 'state' | 't' | 'gait' | 'speed' | 'dir' | 'gone' | 'kind'> {
  const g = new THREE.Group(), coat = mat(0x8a5e3a), pale = mat(0xe8dcc4), dark = mat(0x2a1e16), antler = mat(0xcab79a);
  const body = new THREE.Mesh(pod(1.0, 0.27, 0.85), coat); body.position.set(0, 0.86, 0); body.scale.set(0.9, 1, 1); g.add(body);
  const belly = new THREE.Mesh(pod(0.7, 0.18, 0.9), pale); belly.position.set(0, 0.78, 0.02); belly.scale.set(0.85, 0.8, 1); g.add(belly);
  // neck and head hang off one pivot at the shoulders, so grazing bows the whole neck down
  const pivot = new THREE.Group(); pivot.position.set(0, 0.98, 0.32); g.add(pivot);
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.13, 0.5, 8), coat); neck.position.set(0, 0.12, 0.1); neck.rotation.x = 0.7; pivot.add(neck);
  const head = new THREE.Group(); head.position.set(0, 0.3, 0.24); pivot.add(head);
  const skull = new THREE.Mesh(pod(0.32, 0.1, 0.55), coat); skull.position.set(0, 0, 0.08); head.add(skull);
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.035, 8, 6), dark); nose.position.set(0, -0.02, 0.25); head.add(nose);
  for (const s of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.16, 6), coat); ear.position.set(s * 0.08, 0.08, -0.02); ear.rotation.set(-0.3, 0, s * -0.9); head.add(ear);
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.018, 6, 5), dark); eye.position.set(s * 0.065, 0.03, 0.08); head.add(eye);
    if (stag) {
      // two tines and a beam each side
      const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.02, 0.32, 5), antler); beam.position.set(s * 0.07, 0.2, -0.02); beam.rotation.set(-0.3, 0, s * -0.35); head.add(beam);
      const tine = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.014, 0.16, 5), antler); tine.position.set(s * 0.1, 0.24, 0.05); tine.rotation.set(0.6, 0, s * -0.2); head.add(tine);
      const top = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.012, 0.14, 5), antler); top.position.set(s * 0.14, 0.36, -0.04); top.rotation.set(-0.2, 0, s * -0.6); head.add(top);
    }
  }
  const tail = new THREE.Group(); tail.position.set(0, 0.98, -0.46); g.add(tail);
  const tt = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.14, 6), pale); tt.rotation.x = Math.PI * 0.85; tt.position.y = -0.04; tail.add(tt);
  const legs = [[-0.12, 0.3], [0.12, 0.3], [-0.12, -0.3], [0.12, -0.3]].map(([x, z]) => leg(g, x, 0.78, z, 0.78, 0.055, coat, dark));
  g.traverse(o => { (o as THREE.Mesh).castShadow = true; });
  return { g, head: pivot, legs, tail };
}
export function buildFox(): Omit<Beast, 'x' | 'z' | 'face' | 'state' | 't' | 'gait' | 'speed' | 'dir' | 'gone' | 'kind'> {
  const g = new THREE.Group(), fur = mat(0xc8642a), white = mat(0xf0e8dc), dark = mat(0x2a1a14);
  const body = new THREE.Mesh(pod(0.62, 0.13, 0.8), fur); body.position.set(0, 0.36, 0); g.add(body);
  const chest = new THREE.Mesh(pod(0.3, 0.09, 0.8), white); chest.position.set(0, 0.32, 0.2); g.add(chest);
  const pivot = new THREE.Group(); pivot.position.set(0, 0.4, 0.24); g.add(pivot);
  const head = new THREE.Group(); head.position.set(0, 0.06, 0.12); pivot.add(head);
  const skull = new THREE.Mesh(new THREE.SphereGeometry(0.09, 10, 8), fur); skull.scale.set(1, 0.9, 1.05); head.add(skull);
  const snout = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.16, 8), white); snout.rotation.x = Math.PI / 2; snout.position.set(0, -0.02, 0.12); head.add(snout);
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.018, 6, 5), dark); nose.position.set(0, -0.02, 0.2); head.add(nose);
  for (const s of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.ConeGeometry(0.035, 0.11, 4), fur); ear.position.set(s * 0.05, 0.1, -0.01); ear.rotation.z = s * -0.25; head.add(ear);
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.016, 0.04, 4), dark); tip.position.set(s * 0.057, 0.155, -0.01); tip.rotation.z = s * -0.25; head.add(tip);
  }
  const tail = new THREE.Group(); tail.position.set(0, 0.38, -0.3); g.add(tail);
  const brush = new THREE.Mesh(pod(0.38, 0.07, 0.6), fur); brush.position.set(0, -0.05, -0.18); brush.rotation.x = 0.35; tail.add(brush);
  const btip = new THREE.Mesh(new THREE.SphereGeometry(0.04, 8, 6), white); btip.position.set(0, -0.11, -0.37); tail.add(btip);
  const legs = [[-0.07, 0.18], [0.07, 0.18], [-0.07, -0.18], [0.07, -0.18]].map(([x, z]) => leg(g, x, 0.3, z, 0.3, 0.025, dark, dark));
  g.traverse(o => { (o as THREE.Mesh).castShadow = true; });
  return { g, head: pivot, legs, tail };
}

export class Wildlife {
  private beasts: Beast[] = [];
  private next = 2;
  constructor(private scene: THREE.Scene, private map: GameMap, private max = 6) {}

  private open(x: number, z: number) {
    const tx = Math.floor(x), tz = Math.floor(z);
    if (!this.map.inBounds(tx, tz)) return false;
    const t = this.map.idx(tx, tz);
    return this.map.walkable(t) && this.map.objAt[t] < 0 && (this.map.terrain[t] === TERRAIN.grass || this.map.terrain[t] === TERRAIN.path);
  }
  private wild(x: number, z: number) { const r = this.map.regionAt(Math.floor(x), Math.floor(z)); return !!r && WILD_REGIONS.has(r.key); }

  private spawn(tx: number, tz: number, night: number) {
    const fox = Math.random() < 0.18 + night * 0.4;
    for (let tries = 0; tries < 12; tries++) {
      const a = Math.random() * Math.PI * 2, d = 18 + Math.random() * 12, x = tx + Math.cos(a) * d, z = tz + Math.sin(a) * d;
      if (!this.open(x, z) || !this.wild(x, z)) continue;
      const n = fox ? 1 : 2 + Math.floor(Math.random() * 2);
      for (let i = 0; i < n; i++) {
        const bx = x + (Math.random() - 0.5) * 3, bz = z + (Math.random() - 0.5) * 3;
        if (!this.open(bx, bz)) continue;
        const parts = fox ? buildFox() : buildDeer(i === 0 && Math.random() < 0.6);
        const s = fox ? 1 : 0.9 + Math.random() * 0.15; parts.g.scale.setScalar(s);
        this.scene.add(parts.g);
        this.beasts.push({ kind: fox ? 'fox' : 'deer', ...parts, x: bx, z: bz, face: Math.random() * Math.PI * 2, state: 'graze', t: Math.random() * 3, gait: Math.random() * 6, speed: 0, dir: 0, gone: false });
      }
      return;
    }
  }

  update(dt: number, time: number, tx: number, tz: number, night: number, walkers: Walker[]) {
    this.next -= dt;
    const want = Math.round(this.max * (0.5 + night * 0.2));
    if (this.next <= 0) { this.next = 6 + Math.random() * 6; if (this.beasts.length < want) this.spawn(tx, tz, night); }
    for (const b of this.beasts) {
      // nearest person, and how fast they're coming
      let near = Infinity, nx = 0, nz = 0;
      for (const w of walkers) { const d = Math.hypot(w.x - b.x, w.z - b.z); if (d < near) { near = d; nx = w.x; nz = w.z; } }
      const shy = b.kind === 'fox' ? 1.25 : 1;
      b.t -= dt;
      if (b.state !== 'flee' && near < 6.5 * shy) { b.state = 'flee'; b.dir = Math.atan2(b.x - nx, b.z - nz) + (Math.random() - 0.5) * 0.6; b.t = 4 + Math.random() * 2; }
      else if ((b.state === 'graze' || b.state === 'walk') && near < 11 * shy) { b.state = 'alert'; b.t = 2 + Math.random() * 2; }
      if (b.t <= 0) {
        if (b.state === 'flee') { b.state = 'alert'; b.t = 2; }
        else if (b.state === 'alert') b.state = near < 11 * shy ? 'alert' : 'graze', b.t = 2 + Math.random() * 2;
        else if (b.state === 'graze') { b.state = 'walk'; b.dir = b.face + (Math.random() - 0.5) * 2; b.t = 1.5 + Math.random() * 2; }
        else { b.state = 'graze'; b.t = 3 + Math.random() * 5; }
      }
      const run = b.kind === 'fox' ? 5.5 : 6.5, target = b.state === 'flee' ? run : b.state === 'walk' ? (b.kind === 'fox' ? 1.4 : 0.9) : 0;
      b.speed += (target - b.speed) * Math.min(1, dt * 4);
      if (b.state === 'alert') { b.face = turn(b.face, Math.atan2(nx - b.x, nz - b.z), dt * 3); }
      else if (b.speed > 0.05) {
        b.face = turn(b.face, b.dir, dt * (b.state === 'flee' ? 6 : 2));
        const ax = b.x + Math.sin(b.face) * b.speed * dt, az = b.z + Math.cos(b.face) * b.speed * dt;
        if (this.open(ax, az)) { b.x = ax; b.z = az; } else b.dir += (Math.random() < 0.5 ? 1 : -1) * 1.2;
      }
      // the gait: a graceful bound for a fleeing deer, a trot for a fox, a slow walk otherwise
      b.gait += dt * (2 + b.speed * (b.kind === 'deer' && b.state === 'flee' ? 1.6 : 2.4));
      const bound = b.kind === 'deer' && b.state === 'flee', sw = Math.sin(b.gait);
      const k = Math.min(1, b.speed / 2);
      b.legs.forEach((l, i) => { const front = i < 2; l.rotation.x = bound ? (front ? -0.7 - sw * 0.5 : 0.7 + sw * 0.5) * k : sw * (i % 2 === (front ? 0 : 1) ? 1 : -1) * 0.5 * k; });
      const hop = bound ? Math.max(0, Math.sin(b.gait)) * 0.28 : 0;
      const grazing = b.state === 'graze' && Math.sin(time * 0.6 + b.gait) > -0.3;
      b.head.rotation.x += ((grazing ? (b.kind === 'deer' ? 1.15 : 0.8) : b.state === 'alert' ? -0.3 : 0) - b.head.rotation.x) * Math.min(1, dt * 3);
      b.tail.rotation.x = b.state === 'flee' ? (b.kind === 'deer' ? -1.2 : -0.4) : Math.sin(time * 3 + b.gait) * 0.15;
      const y = this.map.heightAt(b.x, b.z);
      b.g.position.set(b.x, y + hop, b.z);
      b.g.rotation.set(bound ? -sw * 0.12 : 0, b.face, 0);
      // wandered off, or we did
      if (Math.hypot(b.x - tx, b.z - tz) > 48) b.gone = true;
    }
    for (const b of this.beasts) if (b.gone) { this.scene.remove(b.g); b.g.traverse(o => (o as THREE.Mesh).geometry?.dispose()); }
    this.beasts = this.beasts.filter(b => !b.gone);
  }
}
function turn(a: number, to: number, k: number) {
  let d = to - a; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
  return a + d * Math.min(1, k);
}
